import {CancelStartSpinReason, GlobalEventsMap} from '@gongxigames/framework/slot/events';
import {CommandLineInterface, CoreDI, DataStorage, DisplayObjectUtils, inject} from '@gongxigames/framework/core';
import {AutoplayOptions, AutoplayOptionsOptionsKeys} from '@gongxigames/framework/slot/types';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {
    AutoplayOptionsDataStorage, AutoplayOptionsPopup,
    AutoplayShowOptionsPopupProperties,
    AutoplayStoredValues
} from '@gongxigames/framework/slot/modules/autoplayOptions/AutoplayOptionsPopup';
import {AutoplayOptionsPopupView} from '@gongxigames/framework/slot/modules/autoplayOptions/popup/AutoplayOptionsPopupView';

export class AutoplayOptionsModule extends Module<null, GlobalEventsMap> {

    public static readonly NAME: symbol = Symbol('AutoplayOptionsModule');

    private _property!: AutoplayShowOptionsPopupProperties;

    @inject(CoreDI.DisplayObjectUtils)
    private _displayObjectUtils!: DisplayObjectUtils;

    @inject(CoreDI.CLI)
    private _cli!: CommandLineInterface;

    private _options: AutoplayOptionsDataStorage = new DataStorage({
        winLimit: null,
        singleWin: null,
        lossLimit: null,
        turboMode: false,
        autoplaySpins: null
    });

    private _popup = {
        id: 'AutoplayOptionsPopup',
        view: AutoplayOptionsPopupView,
        type: 'ReactPIXI'
    };

    protected setupEvents(): void {
        super.setupEvents();

        this.on('Autoplay.SHOW_OPTIONS_POPUP', this.showPopup);
        this.on('Autoplay.HIDE_OPTIONS_POPUP', this.closePopup);

        /* develblock:start */
        this._cli.addCommand('autoPlay', {
            show: this.showPopup.bind(this),
            close: this.closePopup.bind(this)
        });
        /* develblock:end */
    }

    private showPopup(_id: any, property: AutoplayShowOptionsPopupProperties): void {
        // TODO this.logic.canSpin(this.logic.getTotalBet()) move to logic.isLowBalance()
        if (!this.logic.canSpin(this.logic.getTotalBet())) {
            this.eventManager.emit('General.Flow.cancelStartSpin', {
                reason: CancelStartSpinReason.INSUFFICIENT_FUNDS,
                balanceRefreshCallback: this.showPopup.bind(this, _id, property)
            });

            return;
        }

        this._property = property;

        this.resetAutoplayOptions();
        this.emitAddAutoplayOptionsPopup();
    }

    private closePopup(): void {
        this.emitRemoveAutoplayOptionsPopup();
        this.resetAutoplayOptions();
    }

    private onClickClosePopup(): void {
        this.closePopup();
    }

    private onClickStartAutoplay(): void {
        this.checkRequiredOptions();

        if (!this.hasNotValidValue()) {
            this.eventManager.emit('Autoplay.START', {
                winLimit: this._options.get('winLimit'),
                singleWin: this._options.get('singleWin'),
                lossLimit: this._options.get('lossLimit'),
                autoplaySpins: this._options.get('autoplaySpins'),
                turboMode: this._options.get('turboMode')
            });

            this.closePopup();
        }
    }

    private setOption<K extends keyof AutoplayOptions>(key: K, value: AutoplayOptions[K]): void {
        this._options.set(key, value);
        this.getEventManager().emit('Autoplay.CHANGED_OPTION', {[key]: value});
    }

    private getDefaultStoredOptions(defaultOptions: any = {}): AutoplayStoredValues {
        const {
            winLimit = null,
            singleWin = null,
            lossLimit = null,
            autoplaySpins = null,
            turboMode = false,
            notValidValues = []
        } = defaultOptions || {};

        return {winLimit, singleWin, lossLimit, autoplaySpins, notValidValues, turboMode};
    }

    private resetAutoplayOptions(): void {
        const {
            winLimit = null,
            singleWin = null,
            lossLimit = null,
            autoplaySpins = null,
            notValidValues = [],
            turboMode
        } = this.getDefaultStoredOptions(this._property.defaultOptions);

        this._options.set('winLimit', winLimit);
        this._options.set('singleWin', singleWin);
        this._options.set('lossLimit', lossLimit);
        this._options.set('turboMode', turboMode);
        this._options.set('autoplaySpins', autoplaySpins);
        this._options.set('notValidValues', notValidValues);
    }

    private emitAddAutoplayOptionsPopup(): void {
        this._eventManager.emit('General.Popups.Add', {
            ...this._popup,
            data: {
                options: this._options,
                setOption: this.setOption.bind(this),
                turboModeDisabled: this._property.disabledTurboButton,
                disabledLimits: this._property.disabledLimits,
                autoplayShowBetAndWager: this._property.autoplayShowBetAndWager,
                autoplayShowConfirmationPopup: this._property.autoplayShowConfirmationPopup,
                totalBet: this.logic.getTotalBet(),
                roundsOptions:  this._property.autoplayValues,
                startAutoplay: this.onClickStartAutoplay.bind(this),
                closeAutoplayOptionsPopup: this.onClickClosePopup.bind(this)
            }
        } as AutoplayOptionsPopup);
    }

    private emitRemoveAutoplayOptionsPopup(): void {
        this._eventManager.emit('General.Popups.Remove', this._popup);
    }

    private hasNotValidValue(): boolean {
        const {notValidValues = []} = this._options.data;

        return notValidValues.length > 0;
    }

    private checkRequiredOptions(): void {
        const notValidValues: AutoplayOptionsOptionsKeys[] = [];
        const {requiredOptions = []} = this._property;

        requiredOptions.forEach(requiredOption => {
            const value = this._options.get(requiredOption);
            const hasValue = (value: number | boolean | null): boolean => {
                if (typeof value === 'number') {
                    return value > 0;
                } else if (typeof value === 'boolean') {
                    return value;
                }

                return false;
            };

            if (!hasValue(value)) {
                notValidValues.push(requiredOption);

                const waitFunction = this.waitRequiredValue.bind(this, hasValue, requiredOption);

                this._options.on(requiredOption, waitFunction, this);
            }
        });

        this._options.set('notValidValues', notValidValues);
    }

    private waitRequiredValue(isValid: (value: any) => boolean, key: string, value: any): void {
        const {notValidValues = []} = this._options.data;

        if (isValid(value)) {
            this._options.off(key as any, this);
            this._options.set('notValidValues', notValidValues.filter(option => option !== key));
        }
    }
}
